import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import DatePicker from "@/components/DatePicker";

const EMPTY = {
  title: "",
  country: "",
  certificate_type: "",
  issuer: "",
  issued_date: "",
  expiry_date: "",
  description: "",
  image_url: "",
  document_url: "",
  badge_color: "purple",
  verified: true,
  public_visible: true,
  protected_preview: true,
  sort_order: 0,
};

export default function AdminCertificates() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: certs = [], isLoading } = useQuery({
    queryKey: ["admin-certificates"],
    queryFn: async () => (await api.get("/admin/certificates")).data,
  });

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      const url = editing ? `/admin/certificates/${editing.id}` : "/admin/certificates";
      const method = editing ? "put" : "post";
      return (await api[method](url, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-certificates"] });
      qc.invalidateQueries({ queryKey: ["public-certificates"] });
      toast.success(editing ? "Certificate updated" : "Certificate created");
      setOpen(false);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/certificates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-certificates"] });
      qc.invalidateQueries({ queryKey: ["public-certificates"] });
      toast.success("Certificate deleted");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY, ...c });
    setOpen(true);
  };
  const submit = (e) => {
    e.preventDefault();
    saveMut.mutate(form);
  };

  return (
    <div data-testid="admin-certificates">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="badge-purple">
            <ShieldCheck className="h-3.5 w-3.5" /> Trust Center CMS
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            Certificates
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-600">
            Upload country-specific licenses and chamber certificates here. Public-visible items
            appear automatically on the Trust Center with your chosen order and description.
          </p>
        </div>
        <button onClick={openCreate} className="btn-primary" data-testid="add-certificate-button">
          <Plus className="h-4 w-4" /> New Certificate
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {isLoading ? (
          <div className="col-span-full text-center py-10 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin inline" />
          </div>
        ) : (
          certs.map((c) => (
            <div key={c.id} className="rsi-card overflow-hidden" data-testid={`cert-${c.id}`}>
              <div className="aspect-[5/3] bg-gray-100 overflow-hidden">
                <img src={c.image_url} alt={c.title} className="h-full w-full object-cover" />
              </div>
              <div className="p-5">
                <div className="font-display font-extrabold text-base tracking-tight text-[var(--rsi-navy)]">
                  {c.title}
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                  {c.country ? <span>{c.country}</span> : null}
                  {c.certificate_type ? <span>{c.certificate_type}</span> : null}
                </div>
                <div className="text-xs text-gray-500 mt-1">{c.issuer}</div>
                <p className="text-xs text-gray-600 mt-2 line-clamp-2">{c.description}</p>
                <div className="mt-3 flex items-center justify-between text-[10px] font-mono-plex text-gray-500">
                  <span>{c.issued_date || "—"}</span>
                  <span>{c.expiry_date || "No expiry"}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-[10px]">
                  <span className="rounded-full bg-[var(--rsi-bg-subtle)] px-2 py-1 text-gray-600">
                    {c.public_visible !== false ? "Public" : "Hidden"}
                  </span>
                  <span className="rounded-full bg-[var(--rsi-bg-subtle)] px-2 py-1 text-gray-600">
                    {c.protected_preview !== false ? "Protected preview" : "Standard preview"}
                  </span>
                  <span className="rounded-full bg-[var(--rsi-bg-subtle)] px-2 py-1 text-gray-600">
                    Order {Number(c.sort_order || 0)}
                  </span>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <button onClick={() => openEdit(c)} className="btn-ghost !py-2 !px-3 text-xs" data-testid={`edit-cert-${c.id}`}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete "${c.title}"?`)) delMut.mutate(c.id);
                    }}
                    className="btn-ghost !py-2 !px-3 text-xs hover:!text-red-600 hover:!border-red-300"
                    data-testid={`delete-cert-${c.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="cert-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
              {editing ? "Edit Certificate" : "New Certificate"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid gap-4 mt-4">
            <div>
              <Label>Title *</Label>
              <Input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1.5"
                data-testid="cert-form-title"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Country</Label>
                <Input
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="mt-1.5"
                  placeholder="Bangladesh"
                />
              </div>
              <div>
                <Label>Certificate Type</Label>
                <Input
                  value={form.certificate_type}
                  onChange={(e) => setForm({ ...form, certificate_type: e.target.value })}
                  className="mt-1.5"
                  placeholder="Trade License / Chamber Membership"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Issuer</Label>
                <Input
                  value={form.issuer}
                  onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value || 0) })}
                  className="mt-1.5"
                  min="0"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Badge Color</Label>
                <Select value={form.badge_color} onValueChange={(v) => setForm({ ...form, badge_color: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="purple">Purple</SelectItem>
                    <SelectItem value="navy">Navy</SelectItem>
                    <SelectItem value="green">Green</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Document URL (optional)</Label>
                <Input
                  value={form.document_url}
                  onChange={(e) => setForm({ ...form, document_url: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid gap-3 rounded-2xl border border-gray-100 bg-[var(--rsi-bg-subtle)] p-4">
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>
                  <span className="font-medium text-[var(--rsi-navy)]">Show on public Trust Center</span>
                  <span className="block text-xs text-gray-500">Only enabled items appear on the public website.</span>
                </span>
                <Switch checked={form.public_visible} onCheckedChange={(v) => setForm({ ...form, public_visible: v })} />
              </label>
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>
                  <span className="font-medium text-[var(--rsi-navy)]">Protected preview mode</span>
                  <span className="block text-xs text-gray-500">Shows watermark/preview-only behavior on the public page.</span>
                </span>
                <Switch checked={form.protected_preview} onCheckedChange={(v) => setForm({ ...form, protected_preview: v })} />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Issued Date</Label>
                <div className="mt-1.5">
                  <DatePicker
                    value={form.issued_date}
                    onChange={(d) => setForm({ ...form, issued_date: d })}
                    placeholder="Select issue date"
                    testId="cert-form-issued-date"
                  />
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Expiry Date</Label>
                <div className="mt-1.5">
                  <DatePicker
                    value={form.expiry_date}
                    onChange={(d) => setForm({ ...form, expiry_date: d })}
                    placeholder="No expiry"
                    testId="cert-form-expiry-date"
                  />
                </div>
              </div>
              <div>
                <label className="flex items-center gap-3 text-sm pt-8">
                  <Switch checked={form.verified} onCheckedChange={(v) => setForm({ ...form, verified: v })} />
                  <span>Verified</span>
                </label>
              </div>
            </div>
            <div>
              <ImageUpload
                label="Certificate Image / Photo of Document"
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
                testId="cert-image-upload"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={saveMut.isPending} className="btn-primary disabled:opacity-60" data-testid="cert-form-submit">
                {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {editing ? "Save changes" : "Create certificate"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
